import * as React from 'react';

import { useLoaderData, useNavigate, useRevalidator } from 'react-router-dom';

import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import EwpFab from "../components/EwpFab";
import Header from "../components/Header";
import Subheader from "../components/Subheader";
import FormFileUpload from "../components/form/FormFileUpload";

import UserCard from "../components/cards/UserCard";

import { useAuth } from '../context/AuthProvider';
import { useMessage } from '../context/MessageProvider';
import { useConnectivity } from '../context/ConnectivityProvider';

import { listUsers, createUser, saveUser } from '../models/users';
import { downloadBlob } from '../utils/download';

export async function loader() {
    const users = await listUsers();
    return {users};
}

const importColumns = ['email', 'name', 'password', 'role'];
const requiredImportColumns = ['email', 'name', 'password', 'role'];
const validRoles = ['keeper', 'admin'];

const parseCsvText = (csvText) => {
  const rows = [];
  let currentCell = '';
  let currentRow = [];
  let inQuotes = false;

  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index];
    const nextChar = csvText[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentCell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      currentRow.push(currentCell);
      currentCell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }

      currentRow.push(currentCell);
      const normalizedRow = currentRow.map((value) => value.trim());
      if (normalizedRow.some((value) => value !== '')) {
        rows.push(normalizedRow);
      }

      currentCell = '';
      currentRow = [];
      continue;
    }

    currentCell += char;
  }

  if (inQuotes) {
    throw new Error('The CSV file could not be parsed because it contains an unclosed quoted value.');
  }

  if (currentCell !== '' || currentRow.length > 0) {
    currentRow.push(currentCell);
    const normalizedRow = currentRow.map((value) => value.trim());
    if (normalizedRow.some((value) => value !== '')) {
      rows.push(normalizedRow);
    }
  }

  return rows;
};

export default function UsersListPage({title = "Users",...props}) {
    const navigate = useNavigate();
    const revalidator = useRevalidator();

    const {users} = useLoaderData();
    const { facilityId, session } = useAuth();
    const { setSuccess, setError } = useMessage();
    const { isOnline } = useConnectivity();

    const [importFile, setImportFile] = React.useState(null);
    const [isImportOpen, setIsImportOpen] = React.useState(false);

    const navigateToUser = (id) => {
        navigate("/users/" + id);
    };

    const handleToggleImport = () => {
        setIsImportOpen((cur) => !cur);
    };

    const handleDownloadTemplate = () => {
        try {
            const csvContent = importColumns.join(',') + '\n';
            const uint8 = new TextEncoder().encode(csvContent);
            const blob = new Blob([uint8], { type: 'text/csv' });
            downloadBlob({ blob, filename: 'users-template.csv' });
            setSuccess('Downloaded users template');
        } catch (error) {
            console.error(error);
            setError(error);
        }
    };

    const handleImportFileChange = (event) => {
        const nextFile = event.target.files?.[0] || null;
        setImportFile(nextFile);
    };

    const handleImportSubmit = async (event) => {
        event.preventDefault();

        if (!importFile) {
            setError('Please choose a CSV file before importing.');
            return;
        }

        const isCsvFile = importFile.name.toLowerCase().endsWith('.csv')
            || importFile.type === 'text/csv'
            || importFile.type === 'application/vnd.ms-excel';

        if (!isCsvFile) {
            setError(`The selected file must be a CSV file. Received: ${importFile.name}`);
            return;
        }

        try {
            const fileContents = await importFile.text();
            const rows = parseCsvText(fileContents);

            if (!rows.length) {
                setError('The CSV file is empty.');
                return;
            }

            const headerRow = rows[0].map((value) => value.trim().toLowerCase());

            if (headerRow.length !== importColumns.length) {
                setError(
                    `The CSV must contain exactly ${importColumns.length} columns. Found ${headerRow.length}: ${headerRow.join(', ')}`
                );
                return;
            }

            const missingHeaders = importColumns.filter((col) => !headerRow.includes(col));
            if (missingHeaders.length) {
                setError(
                    `The CSV is missing required columns: ${missingHeaders.join(', ')}. Found: ${headerRow.join(', ')}`
                );
                return;
            }

            const dataRows = rows.slice(1).map((row, rowIndex) => {
                if (row.length !== importColumns.length) {
                    throw new Error(
                        `Row ${rowIndex + 2} has ${row.length} columns. Expected ${importColumns.length}.`
                    );
                }
                return headerRow.reduce((acc, header, index) => {
                    acc[header] = row[index] || '';
                    return acc;
                }, {});
            });

            if (!dataRows.length) {
                setError('The CSV file contains no data rows to import.');
                return;
            }

            dataRows.forEach((row, rowIndex) => {
                const rowNum = rowIndex + 2;

                requiredImportColumns.forEach((col) => {
                    if (!row[col] || !row[col].trim()) {
                        throw new Error(`Row ${rowNum} is missing a required value for: ${col}`);
                    }
                });

                if (!validRoles.includes(row.role.trim().toLowerCase())) {
                    throw new Error(
                        `Row ${rowNum} has an invalid role: "${row.role}". Must be "keeper" or "admin".`
                    );
                }
            });

            let createdCount = 0;
            for (const row of dataRows) {
                const createResult = await createUser(
                    { email: row.email.trim(), password: row.password, name: row.name.trim() },
                    facilityId,
                    session
                );
                const userId = createResult.data.user.id;

                await saveUser(
                    {
                        id: userId,
                        email: row.email.trim(),
                        name: row.name.trim(),
                        role: row.role.trim().toLowerCase(),
                    },
                    facilityId,
                    isOnline
                );

                createdCount++;
            }

            setSuccess(`Imported ${createdCount} user${createdCount !== 1 ? 's' : ''}`);
            setImportFile(null);
            revalidator.revalidate();
        } catch (error) {
            console.error(error);
            setError(error.message || 'The CSV file could not be parsed.');
        }
    };

    return (
        <Container component="main" maxWidth="md">
            <Header label={title} />

            <div
                onClick={handleToggleImport}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleToggleImport();
                    }
                }}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
            >
                <Grid container alignItems="center" spacing={1}>
                    <Grid xs>
                        <Subheader label="Import" />
                    </Grid>
                    <Grid>
                        <ExpandMoreIcon
                            sx={{
                                transform: isImportOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                            }}
                        />
                    </Grid>
                </Grid>
            </div>

            <Collapse in={isImportOpen}>
                <form onSubmit={handleImportSubmit}>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Download a template CSV with the required columns for bulk user import.
                            </Typography>
                            <Button type="button" variant="outlined" onClick={handleDownloadTemplate}>
                                Download Template CSV
                            </Button>
                        </Grid>
                        <Grid xs={12} sm={8}>
                            <FormFileUpload
                                label="Users CSV"
                                buttonLabel="Choose file"
                                file={importFile}
                                accept=".csv,text/csv"
                                onChange={handleImportFileChange}
                            />
                        </Grid>
                        <Grid xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                            <Button type="submit" variant="contained" fullWidth>
                                Import
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Collapse>

            <Grid container spacing={2}>
            {users.map((user)=>(
                <Grid key={user.id} xs={12} sm={6} md={4}>
                    <UserCard user={user} onActionAreaClick={()=>{navigateToUser(user.id)}} />
                </Grid>
            ))}
            </Grid>

            <EwpFab onClick={()=>navigate("/users/create")}>
                <AddIcon />
            </EwpFab>
        </Container>
    );
}
